// components/common/ImageUploadField.jsx

import React, { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";

const DEFAULT_ACCEPT = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

const ImageUploadField = ({
  label = "Upload Image",
  value,
  onChange,

  /* VALIDATION */
  maxSize = 5, // MB
  accept = DEFAULT_ACCEPT,

  /* UI */
  hint,
  disabled = false,
  required = false,
  error,
  previewAlt = "Preview",
  className = "",

  /* BEHAVIOUR */
  showPreview = true,
}) => {
  const fileRef = useRef(null);

  const [localError, setLocalError] = useState("");

  const acceptString = Array.isArray(accept) ? accept.join(",") : accept;

  const maxFileSize = maxSize * 1024 * 1024;

  const resolvedHint =
    hint ||
    `${acceptString
      .replaceAll("image/", "")
      .replaceAll(",", ", ")
      .toUpperCase()} • Max ${maxSize}MB`;

  /* OPEN PICKER */
  const openFilePicker = () => {
    if (disabled) return;

    fileRef.current?.click();
  };

  /* REMOVE FILE */
  const handleRemove = () => {
    setLocalError("");

    onChange?.(null, null);

    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  /* VALIDATE FILE */
  const validateFile = (file) => {
    if (!file) return "No file selected";

    /* TYPE */
    const allowedTypes = Array.isArray(accept) ? accept : accept.split(",");

    if (!allowedTypes.includes(file.type)) {
      return "Invalid file type";
    }

    /* SIZE */
    if (file.size > maxFileSize) {
      return `File size must be under ${maxSize}MB`;
    }

    return null;
  };

  /* FILE CHANGE */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const validationError = validateFile(file);

    if (validationError) {
      setLocalError(validationError);

      e.target.value = "";

      return;
    }

    setLocalError("");

    const reader = new FileReader();

    reader.onloadend = () => {
      onChange?.(reader.result, file);
    };

    reader.readAsDataURL(file);
  };

  const displayError = error || localError;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {/* LABEL */}
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* FILLED STATE */}
      {value ? (
        <div
          className="
            flex items-center gap-3 rounded-xl
            border border-slate-200 dark:border-slate-700
            bg-slate-50 dark:bg-slate-800/60
            px-3 py-3
          "
        >
          {/* IMAGE */}
          {showPreview ? (
            <img
              src={value}
              alt={previewAlt}
              className="
                h-12 w-12 shrink-0 rounded-lg object-cover
                border border-slate-200 dark:border-slate-600
              "
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              className="
                flex h-12 w-12 shrink-0 items-center justify-center
                rounded-lg bg-slate-200 dark:bg-slate-700
              "
            >
              <ImageIcon className="h-5 w-5 text-slate-500" />
            </div>
          )}

          {/* TEXT */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
              Image selected
            </p>

            <p className="mt-0.5 text-xs text-slate-400">
              Click change to replace image
            </p>
          </div>

          {/* ACTIONS */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={openFilePicker}
              className="
                rounded-lg border border-slate-300 dark:border-slate-600
                bg-white dark:bg-slate-700
                px-3 py-1.5 text-xs font-medium
                text-slate-700 dark:text-slate-200
                transition-colors
                hover:bg-slate-100 dark:hover:bg-slate-600
                disabled:cursor-not-allowed disabled:opacity-50
              "
            >
              Change
            </button>

            <button
              type="button"
              disabled={disabled}
              onClick={handleRemove}
              className="
                inline-flex items-center gap-1 rounded-lg
                border border-red-200 dark:border-red-800
                bg-red-50 dark:bg-red-900/20
                px-3 py-1.5 text-xs font-medium
                text-red-600 dark:text-red-400
                transition-colors
                hover:bg-red-100 dark:hover:bg-red-900/30
                disabled:cursor-not-allowed disabled:opacity-50
              "
            >
              <X className="h-3.5 w-3.5" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        /* EMPTY STATE */
        <button
          type="button"
          disabled={disabled}
          onClick={openFilePicker}
          className={`
            group flex w-full items-center gap-3 rounded-xl
            border border-dashed px-4 py-4 transition-all
            ${
              displayError
                ? "border-red-300 bg-red-50/40 dark:border-red-800 dark:bg-red-900/10"
                : "border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/40 hover:border-blue-400 hover:bg-blue-50/40"
            }
            disabled:cursor-not-allowed disabled:opacity-60
          `}
        >
          {/* ICON */}
          <div
            className="
              flex h-10 w-10 shrink-0 items-center justify-center
              rounded-lg bg-slate-200 dark:bg-slate-700
              transition-colors
              group-hover:bg-blue-100 dark:group-hover:bg-blue-900/20
            "
          >
            <Upload
              className="
                h-4 w-4 text-slate-500
                group-hover:text-blue-500
              "
            />
          </div>

          {/* CONTENT */}
          <div className="min-w-0 text-left">
            <p
              className="
                text-sm font-semibold
                text-slate-700 dark:text-slate-200
              "
            >
              Upload image
            </p>

            <p className="mt-0.5 text-xs text-slate-400">{resolvedHint}</p>
          </div>
        </button>
      )}

      {/* ERROR */}
      {displayError && (
        <p className="text-xs font-medium text-red-500">{displayError}</p>
      )}

      {/* HIDDEN INPUT */}
      <input
        ref={fileRef}
        type="file"
        hidden
        accept={acceptString}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ImageUploadField;
